import { useParams, useNavigate } from 'react-router-dom'
import { useState, useEffect, Fragment } from 'react'
import {
  ArrowLeft,
  DollarSign,
  Calendar,
  Package,
  Pencil,
  Trash2,
  MapPin,
  FileText,
  MessageSquare,
  Plus,
  AlertTriangle,
  Mail,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { toDisplayDate } from '@/lib/utils'
import { RecordCombobox } from '@/components/ui/record-combobox'
import { useDebounce } from '@/hooks/use-debounce'
import { useClickToEdit } from '@/hooks/use-click-to-edit'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { useToast } from '@/hooks/use-toast'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { useRecordBreadcrumb } from '@/contexts/BreadcrumbContext'
import { EditActionBar } from '@/components/EditActionBar'
import { HistoryTimeline } from '@/components/HistoryTimeline'
import { AddressAutocomplete, type AddressDetails } from '@/components/ui/address-autocomplete'
import { AttachmentsTab } from '@/components/AttachmentsTab'
import { NoteDetailModal } from '@/components/NoteDetailModal'
import { OrderEmailDialog } from '@/components/orders/OrderEmailDialog'
import { OrderStageProgress, allowedNextStatuses } from '@/components/OrderStageProgress'
import {
  matchProductFamily,
  DOMAIN_USERS_LABEL_BY_FAMILY,
  SUBSCRIPTION_TERM_OPTIONS,
} from '@/lib/productFamilyRules'
import {
  useOrder,
  useUpdateOrder,
  useDeleteOrder,
  useCreateOrder,
  useOrderItems,
  useUsers,
  useOpportunities,
  useAccounts,
  useContacts,
  useNotes,
  useCreateNote,
  useDeleteNote,
  useEntityContent,
} from '@/hooks/use-api'
import type { Order, Note } from '@/types'
import { useUser } from '@/contexts/UserContext'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { CATEGORICAL, GRACKLE_COLORS } from '@/lib/theme'
import { explainError } from '@/lib/explain-error'

// Order statuses matching Salesforce
const ORDER_STATUSES = ['Draft', 'Pending', 'Approved', 'Processed', 'Denied']
const ORDER_CURRENCIES = ['USD', 'CAD', 'EUR', 'GBP']

interface OrderDetailProps {
  isNew?: boolean
}

export default function OrderDetailPage({ isNew = false }: OrderDetailProps) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { currentUser, isOrders, isAdmin } = useUser()

  const { data: order, isLoading, error } = useOrder(id!)
  useDocumentTitle(
    order ? `${order.name || `Order ${order.order_number ?? ''}`.trim()} | Order` : undefined
  )
  useRecordBreadcrumb(order ? order.name || `Order ${order.order_number ?? ''}`.trim() : undefined)
  const { data: orderItemsData } = useOrderItems(id!)
  const { data: usersData } = useUsers({ limit: 10000 })
  const { data: opportunitiesData } = useOpportunities({ limit: 10000 })
  const { data: accountsData } = useAccounts({ limit: 10000 })

  const updateOrderMutation = useUpdateOrder()
  const deleteOrderMutation = useDeleteOrder()
  const createOrderMutation = useCreateOrder()

  // Notes hooks
  const { data: notesData } = useNotes({ parent_id: id! })
  const createNoteMutation = useCreateNote(id)
  const deleteNoteMutation = useDeleteNote(id)
  const notes: Note[] = notesData?.data ?? []

  // Files hooks
  const { data: filesData } = useEntityContent(id!)
  const files = filesData?.data ?? []

  // Notes UI state
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null)
  const [noteModalOpen, setNoteModalOpen] = useState(false)
  const [showNoteForm, setShowNoteForm] = useState(false)
  const [newNoteTitle, setNewNoteTitle] = useState('')
  const [newNoteBody, setNewNoteBody] = useState('')

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(isNew)
  const [editForm, setEditForm] = useState<Partial<Order>>({})
  const [editFormErrors, setEditFormErrors] = useState<Record<string, string>>({})

  // Contact search for Bill To Contact
  const [billContactSearch, setBillContactSearch] = useState('')
  const debouncedBillContactSearch = useDebounce(billContactSearch, 300)
  const { data: billContactsData, isFetching: billContactsLoading } = useContacts({
    search: debouncedBillContactSearch || undefined,
    limit: 10000,
    sort_by: 'last_name',
    sort_order: 'asc' as const,
    account_id: editForm.account_id || order?.account_id || undefined,
  })

  // Contact search for Customer Authorized By
  const [custAuthSearch, setCustAuthSearch] = useState('')
  const debouncedCustAuthSearch = useDebounce(custAuthSearch, 300)
  const { data: custAuthContactsData, isFetching: custAuthContactsLoading } = useContacts({
    search: debouncedCustAuthSearch || undefined,
    limit: 10000,
    sort_by: 'last_name',
    sort_order: 'asc' as const,
    account_id: editForm.account_id || order?.account_id || undefined,
  })

  // Initialize form for new order
  useEffect(() => {
    if (isNew && currentUser) {
      setEditForm({
        owner_id: currentUser.id,
        status: 'Draft',
        currency_iso_code: 'USD',
      })
    }
  }, [isNew, currentUser])

  // Related data arrays
  const users = usersData?.data ?? []
  const opportunities = opportunitiesData?.data ?? []
  const accounts = accountsData?.data ?? []
  const billContacts = billContactsData?.data ?? []
  const custAuthContacts = custAuthContactsData?.data ?? []
  const orderItems = orderItemsData ?? []

  const formatCurrency = (amount?: number) => {
    const currency = order?.currency_iso_code || 'USD'
    const value = amount || 0
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value)
    } catch {
      // Intl throws on invalid/junk ISO codes — fall back to numeric + code.
      return `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)} ${currency}`
    }
  }

  const formatDate = (date?: string) => {
    const d = toDisplayDate(date)
    if (!d) return 'N/A'
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatDateTime = (date?: string) => {
    if (!date) return 'N/A'
    return (toDisplayDate(date) ?? new Date(date)).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  // Denial modal state
  const [showDenialModal, setShowDenialModal] = useState(false)
  // Controlled confirmation for high-consequence status changes (Process / Revert).
  const [pendingConfirm, setPendingConfirm] = useState<{
    run: () => void
    title: string
    description: string
    actionLabel: string
  } | null>(null)
  const [denialNotes, setDenialNotes] = useState('')
  const [isDenying, setIsDenying] = useState(false)

  // Commit a status change to the API (no confirmation — callers gate as needed).
  // Opened from the header, and offered automatically once an order is Processed:
  // finalising is the moment the customer email is wanted.
  const [showEmailDialog, setShowEmailDialog] = useState(false)

  const commitStageChange = async (newStage: string) => {
    if (!id) return
    try {
      await updateOrderMutation.mutateAsync({
        id,
        data: { status: newStage },
      })
      toast({
        title: 'Status Updated',
        description: `Order status changed to ${newStage}.`,
      })
      // Offered rather than forced: the email is the next thing that happens after an
      // order is finalised, but not every processed order needs one sent right now.
      if (newStage === 'Processed' && order?.bill_to_contact_email && (isOrders || isAdmin)) {
        setShowEmailDialog(true)
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } } }
      const errorMessage = axiosError?.response?.data?.error || 'Failed to update status'
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Stage change handler (from stepper)
  const handleStageChange = async (newStage: string) => {
    if (!id) return

    // Show denial modal instead of directly changing to Denied
    if (newStage === 'Denied') {
      setShowDenialModal(true)
      return
    }

    // Processing is final and locks the order — confirm first.
    if (newStage === 'Processed') {
      setPendingConfirm({
        run: () => commitStageChange('Processed'),
        title: 'Process this order?',
        description:
          'Once an order is Processed it is final — the order is locked and its status can no longer be changed. Are you absolutely sure?',
        actionLabel: 'Yes, process order',
      })
      return
    }

    // Reverting a processed order back to Pending — confirm first.
    if (order?.status === 'Processed' && newStage === 'Pending') {
      setPendingConfirm({
        run: () => commitStageChange('Pending'),
        title: 'Revert this order to Pending?',
        description:
          'This re-opens a processed order for editing and re-processing. Are you sure you want to revert it?',
        actionLabel: 'Revert to Pending',
      })
      return
    }

    await commitStageChange(newStage)
  }

  const handleDenyOrder = async () => {
    if (!id || !order) return
    setIsDenying(true)
    try {
      await updateOrderMutation.mutateAsync({
        id,
        data: {
          status: 'Denied',
          description: order.description
            ? `${order.description}\n\n--- Denial Notes ---\n${denialNotes}`
            : `--- Denial Notes ---\n${denialNotes}`,
        },
      })

      // Send notification to order owner
      try {
        const { apiClient } = await import('@/lib/api')
        await (apiClient as any).client.post('/notifications', {
          user_id: order.owner_id,
          type: 'order_denied',
          title: `Order Denied: ${order.order_number || order.name || 'Order'}`,
          message: denialNotes || 'Your order has been denied.',
          entity_type: 'order',
          entity_id: id,
        })
      } catch {
        // Notification is non-blocking
      }

      toast({
        title: 'Order Denied',
        description: 'Order has been denied and the owner has been notified.',
      })

      setShowDenialModal(false)
      setDenialNotes('')
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } } }
      toast({
        title: 'Error',
        description: axiosError?.response?.data?.error || 'Failed to deny order',
        variant: 'destructive',
      })
    } finally {
      setIsDenying(false)
    }
  }

  // Edit mode handlers
  const handleStartEdit = () => {
    if (!order) return
    setEditForm({
      name: order.name || '',
      status: order.status || 'Draft',
      effective_date: order.effective_date ? order.effective_date.split('T')[0] : '',
      end_date: order.end_date ? order.end_date.split('T')[0] : '',
      opportunity_id: order.opportunity_id || '',
      account_id: order.account_id || '',
      owner_id: order.owner_id || currentUser?.id || '',
      po_number: order.po_number || '',
      description: order.description || '',
      currency_iso_code: order.currency_iso_code || 'USD',
      bill_to_contact_id: order.bill_to_contact_id || '',
      customer_authorized_by_id: order.customer_authorized_by_id || '',
      billing_street: order.billing_street || '',
      billing_city: order.billing_city || '',
      billing_state: order.billing_state || '',
      billing_postal_code: order.billing_postal_code || '',
      billing_country: order.billing_country || '',
      shipping_street: order.shipping_street || '',
      shipping_city: order.shipping_city || '',
      shipping_state: order.shipping_state || '',
      shipping_postal_code: order.shipping_postal_code || '',
      shipping_country: order.shipping_country || '',
      memo: order.memo || '',
    })
    setIsEditMode(true)
  }

  const handleCancelEdit = () => {
    if (isNew) {
      navigate('/orders')
      return
    }
    setIsEditMode(false)
    setEditForm({})
    setEditFormErrors({})
  }

  const { bindReadOnly } = useClickToEdit(isEditMode, handleStartEdit)

  const handleSaveEdit = async () => {
    // Validate required fields
    const errors: Record<string, string> = {}
    if (!editForm.account_id) {
      errors.account_id = 'Account is required'
    }
    if (!editForm.status) {
      errors.status = 'Status is required'
    }
    if (!editForm.effective_date) {
      errors.effective_date = 'Order Start Date is required'
    }
    if (!editForm.owner_id) {
      errors.owner_id = 'Owner is required'
    }
    if (!editForm.bill_to_contact_id) {
      errors.bill_to_contact_id = 'Bill To Contact is required'
    }

    if (Object.keys(errors).length > 0) {
      setEditFormErrors(errors)
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      })
      return
    }

    // Proactive double-check: an order's Bill To Contact must have a number (phone OR
    // mobile). Catch it here — before submitting the whole order — when we can see the
    // selected contact's details, rather than only via the backend's late rejection.
    const billContact = billContacts.find((c: any) => c.id === editForm.bill_to_contact_id)
    if (billContact && !billContact.phone?.trim() && !billContact.mobile_phone?.trim()) {
      const name = `${billContact.first_name || ''} ${billContact.last_name || ''}`.trim()
      setEditFormErrors((prev) => ({
        ...prev,
        bill_to_contact_id: 'This contact has no phone or mobile number (required for orders)',
      }))
      toast({
        title: 'Billing contact has no number',
        description: `${name || 'The Bill To Contact'} has no phone or mobile number. Add one to the contact before ${isNew ? 'creating' : 'saving'} the order.`,
        variant: 'destructive',
      })
      return
    }

    const doSave = async () => {
      try {
        if (isNew) {
          const newOrder = await createOrderMutation.mutateAsync(editForm)
          toast({
            title: 'Success',
            description: 'Order created successfully.',
          })
          navigate(`/orders/${newOrder.id}`)
        } else {
          await updateOrderMutation.mutateAsync({
            id: id!,
            data: editForm,
          })
          toast({
            title: 'Success',
            description: 'Order updated successfully.',
          })
          setIsEditMode(false)
          setEditForm({})
          setEditFormErrors({})
        }
      } catch (error) {
        // Surface the backend's specific message (e.g. "Bill To Contact is missing required
        // fields: phone or mobile…") instead of the generic axios "status code 400".
        const d = (error as any)?.response?.data
        const errorMessage =
          d?.error ||
          (Array.isArray(d?.details) && d.details.length ? d.details.join(', ') : undefined) ||
          explainError(error, 'Failed to save order')
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        })
      }
    }

    // Saving a status change to Processed is final and locks the order — confirm first.
    if (!isNew && editForm.status === 'Processed' && order?.status !== 'Processed') {
      setPendingConfirm({
        run: () => doSave(),
        title: 'Process this order?',
        description:
          'Once an order is Processed it is final — the order is locked and its status can no longer be changed. Are you absolutely sure?',
        actionLabel: 'Yes, process order',
      })
      return
    }

    await doSave()
  }

  const handleEditFieldChange = (field: string, value: any) => {
    setEditForm((prev) => ({ ...prev, [field]: value }))
    // Clear error when field is modified
    if (editFormErrors[field]) {
      setEditFormErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  // Handle billing address autocomplete
  const handleBillingAddressSelect = (address: AddressDetails) => {
    setEditForm((prev) => ({
      ...prev,
      billing_street: address.street || '',
      billing_city: address.city || '',
      billing_state: address.state || address.state_code || '',
      billing_postal_code: address.postal_code || '',
      billing_country: address.country || '',
    }))
  }

  // Handle shipping address autocomplete
  const handleShippingAddressSelect = (address: AddressDetails) => {
    setEditForm((prev) => ({
      ...prev,
      shipping_street: address.street || '',
      shipping_city: address.city || '',
      shipping_state: address.state || address.state_code || '',
      shipping_postal_code: address.postal_code || '',
      shipping_country: address.country || '',
    }))
  }

  const handleDelete = async () => {
    try {
      await deleteOrderMutation.mutateAsync(id!)
      toast({
        title: 'Success',
        description: 'Order deleted successfully.',
      })
      navigate('/orders')
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to delete order')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Note handlers
  const handleCreateNote = async () => {
    if (!newNoteBody.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Note body is required.',
        variant: 'destructive',
      })
      return
    }

    try {
      await createNoteMutation.mutateAsync({
        parent_id: id!,
        title: newNoteTitle.trim() || undefined,
        body: newNoteBody.trim(),
        owner_id: currentUser?.id,
      })
      toast({
        title: 'Success',
        description: 'Note created successfully.',
      })
      setNewNoteTitle('')
      setNewNoteBody('')
      setShowNoteForm(false)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to create note')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const handleDeleteNote = async (noteId: string, noteTitle: string) => {
    if (!confirm(`Are you sure you want to delete "${noteTitle || 'this note'}"?`)) return

    try {
      await deleteNoteMutation.mutateAsync(noteId)
      toast({
        title: 'Success',
        description: 'Note deleted successfully.',
      })
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to delete note')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Render editable field helper
  const renderEditableField = (
    label: string,
    field: string,
    value: any,
    type: 'text' | 'date' | 'select' | 'textarea' | 'owner' | 'opportunity' | 'account' = 'text',
    options?: string[],
    required?: boolean
  ) => {
    if (isEditMode) {
      const fieldValue = editForm[field as keyof typeof editForm]
      return (
        <div className="py-2 border-b border-border">
          <dt className="text-sm font-medium text-muted-foreground">
            {label}
            {required && <span className="text-destructive"> *</span>}
          </dt>
          <dd className="mt-0.5">
            {type === 'textarea' ? (
              <textarea
                id={field}
                value={(fieldValue as string) || ''}
                onChange={(e) => handleEditFieldChange(field, e.target.value)}
                className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-invalid={!!editFormErrors[field]}
                aria-describedby={editFormErrors[field] ? `${field}-error` : undefined}
                aria-required={required}
              />
            ) : type === 'select' ? (
              <select
                id={field}
                value={(fieldValue as string) || ''}
                onChange={(e) => handleEditFieldChange(field, e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-invalid={!!editFormErrors[field]}
                aria-describedby={editFormErrors[field] ? `${field}-error` : undefined}
                aria-required={required}
              >
                <option value="">Select...</option>
                {options?.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : type === 'owner' ? (
              <select
                id={field}
                value={(fieldValue as string) || ''}
                onChange={(e) => handleEditFieldChange(field, e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-invalid={!!editFormErrors[field]}
                aria-describedby={editFormErrors[field] ? `${field}-error` : undefined}
                aria-required={required}
              >
                <option value="">Select owner...</option>
                {[...users]
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
              </select>
            ) : type === 'opportunity' ? (
              <select
                id={field}
                value={(fieldValue as string) || ''}
                onChange={(e) => handleEditFieldChange(field, e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-invalid={!!editFormErrors[field]}
                aria-describedby={editFormErrors[field] ? `${field}-error` : undefined}
              >
                <option value="">Select opportunity...</option>
                {[...opportunities]
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((opp) => (
                    <option key={opp.id} value={opp.id}>
                      {opp.name}
                    </option>
                  ))}
              </select>
            ) : type === 'account' ? (
              <select
                id={field}
                value={(fieldValue as string) || ''}
                onChange={(e) => handleEditFieldChange(field, e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-invalid={!!editFormErrors[field]}
                aria-describedby={editFormErrors[field] ? `${field}-error` : undefined}
                aria-required={required}
              >
                <option value="">Select account...</option>
                {[...accounts]
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name}
                    </option>
                  ))}
              </select>
            ) : type === 'date' ? (
              <DatePicker
                id={field}
                value={fieldValue as string}
                onChange={(v) => handleEditFieldChange(field, v ?? '')}
                className="h-9"
                aria-invalid={!!editFormErrors[field]}
                aria-describedby={editFormErrors[field] ? `${field}-error` : undefined}
                aria-required={required}
              />
            ) : (
              <Input
                id={field}
                type="text"
                value={(fieldValue as string) || ''}
                onChange={(e) => handleEditFieldChange(field, e.target.value)}
                className="h-9"
                aria-required={required}
                aria-invalid={!!editFormErrors[field]}
                aria-describedby={editFormErrors[field] ? `${field}-error` : undefined}
              />
            )}
            {editFormErrors[field] && (
              <p id={`${field}-error`} className="text-sm text-destructive mt-1" role="alert">
                {editFormErrors[field]}
              </p>
            )}
          </dd>
        </div>
      )
    }
    // Display mode — clicking the value drops the page into edit mode
    // and focuses this field's input.
    const readProps = bindReadOnly(field, label)
    return (
      <div className="py-2 border-b border-border">
        <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
        <dd
          {...readProps}
          className={`mt-0.5 text-sm flex items-center justify-between gap-2 ${readProps.className}`}
        >
          <span className="min-w-0 break-words whitespace-pre-wrap">{value || 'N/A'}</span>
          <Pencil className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden="true" />
        </dd>
      </div>
    )
  }

  // Loading state for existing orders
  if (!isNew && isLoading) {
    return (
      <div className="flex items-center justify-center h-96" role="status" aria-live="polite">
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"
            aria-hidden="true"
          ></div>
          <p className="mt-2 text-muted-foreground">Loading order...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (!isNew && error) {
    return (
      <div className="flex items-center justify-center h-96" role="alert">
        <div className="text-center">
          <p className="text-destructive">Error loading order</p>
          <Button variant="outline" onClick={() => navigate('/orders')} className="mt-4">
            Back to Orders
          </Button>
        </div>
      </div>
    )
  }

  // Not found state
  if (!isNew && !order) {
    return (
      <div className="flex items-center justify-center h-96" role="alert">
        <div className="text-center">
          <p className="text-muted-foreground">Order not found</p>
          <Button variant="outline" onClick={() => navigate('/orders')} className="mt-4">
            Back to Orders
          </Button>
        </div>
      </div>
    )
  }

  // Get owner name
  const ownerName =
    order?.owner_name ||
    users.find((u) => u.id === (isEditMode ? editForm.owner_id : order?.owner_id))?.name ||
    'Unassigned'

  // Calculate order total from line items
  const calculatedTotal = orderItems.reduce((sum, item) => sum + (item.total_price || 0), 0)

  return (
    <main className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/orders')}
            aria-label="Back to orders"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isNew ? 'New Order' : order?.name || `Order ${order?.order_number}`}
            </h1>
            {!isNew && order?.order_number && (
              <p className="text-sm text-muted-foreground">Order #{order.order_number}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditMode ? (
            <>
              <Button variant="outline" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={createOrderMutation.isPending || updateOrderMutation.isPending}
              >
                {createOrderMutation.isPending || updateOrderMutation.isPending
                  ? 'Saving...'
                  : 'Save'}
              </Button>
            </>
          ) : (
            <>
              {/* Orders and Admin only, matching the API. A rep can work with orders
                  but not email the customer, so showing them a button that 403s would
                  be worse than not showing it. */}
              {(isOrders || isAdmin) && (
                <Button
                  variant="outline"
                  onClick={() => setShowEmailDialog(true)}
                  // No billing contact email means there is nowhere to send it. Order
                  // creation requires one, so this only bites on imported orders.
                  disabled={!order?.bill_to_contact_email}
                  title={
                    order?.bill_to_contact_email
                      ? `Email ${order.bill_to_contact_email}`
                      : 'This order has no billing contact email address'
                  }
                >
                  <Mail className="h-4 w-4 mr-2" aria-hidden="true" />
                  Email customer
                </Button>
              )}
              <Button variant="outline" onClick={handleStartEdit}>
                <Pencil className="h-4 w-4 mr-2" aria-hidden="true" />
                Edit
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">
                    <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Order</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete this order? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </div>

      {/* Order Stage Progress */}
      {!isNew && order && (
        <div className="mb-6">
          <OrderStageProgress
            currentStage={order.status || 'Draft'}
            onStageChange={handleStageChange}
            disabled={updateOrderMutation.isPending}
            canRevertProcessed={isOrders || isAdmin}
          />
        </div>
      )}

      {/* Confirmation for high-consequence status changes (Process / Revert) */}
      <AlertDialog
        open={!!pendingConfirm}
        onOpenChange={(open) => {
          if (!open) setPendingConfirm(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingConfirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{pendingConfirm?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const action = pendingConfirm
                setPendingConfirm(null)
                action?.run()
              }}
            >
              {pendingConfirm?.actionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Key Metrics */}
      {!isNew && (
        <div className="grid gap-6 md:grid-cols-3 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Order Total</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {formatCurrency(order?.total_amount || calculatedTotal)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Start Date</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{formatDate(order?.effective_date)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Line Items</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{orderItems.length}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="details" className="w-full">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          {!isNew && <TabsTrigger value="items">Line Items ({orderItems.length})</TabsTrigger>}
          {!isNew && <TabsTrigger value="notes">Notes ({notes.length})</TabsTrigger>}
          {!isNew && <TabsTrigger value="files">Attachments ({files.length})</TabsTrigger>}
          {!isNew && <TabsTrigger value="history">History</TabsTrigger>}
        </TabsList>

        <TabsContent value="details" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Order Information Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" aria-hidden="true" />
                  Order Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="space-y-0">
                  {renderEditableField(
                    'Order Owner',
                    'owner_id',
                    ownerName,
                    'owner',
                    undefined,
                    true
                  )}
                  {isEditMode &&
                    renderEditableField(
                      'Status',
                      'status',
                      order?.status,
                      'select',
                      // Forward-only: current status + the stages it may advance to
                      // (Approved retired; Processed/Denied are locked terminals).
                      order?.status
                        ? Array.from(new Set([order.status, ...allowedNextStatuses(order.status)]))
                        : ORDER_STATUSES,
                      true
                    )}
                  {!isNew && (
                    <div className="py-2 border-b border-border">
                      <dt className="text-sm font-medium text-muted-foreground">Order Number</dt>
                      <dd className="mt-0.5 text-sm">{order?.order_number || 'Auto-generated'}</dd>
                    </div>
                  )}
                  {renderEditableField('PO Number', 'po_number', order?.po_number, 'text')}
                  {renderEditableField(
                    'Account Name',
                    'account_id',
                    order?.account_name || accounts.find((a) => a.id === order?.account_id)?.name,
                    'account',
                    undefined,
                    true
                  )}
                  {renderEditableField(
                    'Description',
                    'description',
                    order?.description,
                    'textarea'
                  )}
                  {renderEditableField('Memo', 'memo', order?.memo, 'textarea')}
                  {renderEditableField(
                    'Order Currency',
                    'currency_iso_code',
                    order?.currency_iso_code,
                    'select',
                    ORDER_CURRENCIES
                  )}
                  {renderEditableField(
                    'Order Start Date',
                    'effective_date',
                    formatDate(order?.effective_date),
                    'date',
                    undefined,
                    true
                  )}
                  {renderEditableField('End Date', 'end_date', formatDate(order?.end_date), 'date')}
                  {renderEditableField(
                    'Opportunity',
                    'opportunity_id',
                    order?.opportunity_name ||
                      opportunities.find((o) => o.id === order?.opportunity_id)?.name,
                    'opportunity'
                  )}
                </dl>
              </CardContent>
            </Card>

            {/* Bill Information Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" aria-hidden="true" />
                  Bill Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="space-y-0">
                  <div className="py-2 border-b border-border">
                    <dt className="text-sm font-medium text-muted-foreground">Bill To Contact</dt>
                    <dd className="mt-0.5">
                      {isEditMode ? (
                        <RecordCombobox
                          id="bill-to-contact"
                          records={billContacts}
                          value={editForm.bill_to_contact_id || ''}
                          onValueChange={(value) =>
                            handleEditFieldChange('bill_to_contact_id', value)
                          }
                          placeholder="Select contact"
                          searchPlaceholder="Search contacts..."
                          emptyMessage="No contacts found."
                          displayField={(c: any) =>
                            `${c.first_name || ''} ${c.last_name || ''}`.trim()
                          }
                          aria-label="Bill To Contact"
                          onSearchChange={setBillContactSearch}
                          isLoading={billContactsLoading}
                          selectedLabel={order?.bill_to_contact_name || undefined}
                          totalCount={billContactsData?.total}
                        />
                      ) : (
                        <span className="text-sm">{order?.bill_to_contact_name || 'N/A'}</span>
                      )}
                    </dd>
                  </div>
                  <div className="py-2 border-b border-border">
                    <dt className="text-sm font-medium text-muted-foreground">Bill To Phone</dt>
                    <dd className="mt-0.5 text-sm">
                      {order?.bill_to_contact_phone || order?.bill_to_contact_mobile ? (
                        <span className="flex flex-wrap items-center gap-x-2">
                          {order?.bill_to_contact_phone && (
                            <a
                              href={`tel:${order.bill_to_contact_phone}`}
                              className="text-primary hover:underline"
                            >
                              {order.bill_to_contact_phone}
                            </a>
                          )}
                          {order?.bill_to_contact_mobile && (
                            <a
                              href={`tel:${order.bill_to_contact_mobile}`}
                              className="text-primary hover:underline"
                            >
                              {order.bill_to_contact_mobile}
                              <span className="text-xs text-muted-foreground"> (mobile)</span>
                            </a>
                          )}
                        </span>
                      ) : (
                        <span style={{ color: CATEGORICAL.clay.solid }}>
                          No phone or mobile on file
                        </span>
                      )}
                    </dd>
                  </div>
                  <div className="py-2 border-b border-border">
                    <dt className="text-sm font-medium text-muted-foreground">
                      Customer Authorized By
                    </dt>
                    <dd className="mt-0.5">
                      {isEditMode ? (
                        <RecordCombobox
                          id="customer-authorized-by"
                          records={custAuthContacts}
                          value={editForm.customer_authorized_by_id || ''}
                          onValueChange={(value) =>
                            handleEditFieldChange('customer_authorized_by_id', value)
                          }
                          placeholder="Select contact"
                          searchPlaceholder="Search contacts..."
                          emptyMessage="No contacts found."
                          displayField={(c: any) =>
                            `${c.first_name || ''} ${c.last_name || ''}`.trim()
                          }
                          aria-label="Customer Authorized By"
                          onSearchChange={setCustAuthSearch}
                          isLoading={custAuthContactsLoading}
                          selectedLabel={order?.customer_authorized_by_name || undefined}
                          totalCount={custAuthContactsData?.total}
                        />
                      ) : (
                        <span className="text-sm">
                          {order?.customer_authorized_by_name || 'N/A'}
                        </span>
                      )}
                      {order?.customer_authorized_by_email && (
                        <div className="mt-0.5">
                          <a
                            href={`mailto:${order.customer_authorized_by_email}`}
                            className="text-sm text-primary hover:underline"
                          >
                            {order.customer_authorized_by_email}
                          </a>
                        </div>
                      )}
                    </dd>
                  </div>

                  {/* Billing Address with Autocomplete */}
                  {isEditMode ? (
                    <div className="py-2 border-b border-border">
                      <dt className="text-sm font-medium text-muted-foreground mb-1">
                        Billing Address
                      </dt>
                      <dd className="space-y-2">
                        <AddressAutocomplete
                          id="billing_street"
                          label="Billing Street"
                          value={editForm.billing_street || ''}
                          onAddressSelect={handleBillingAddressSelect}
                          onStreetChange={(street) =>
                            handleEditFieldChange('billing_street', street)
                          }
                          countryRestrictions={['us', 'ca']}
                          placeholder="Start typing address..."
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-muted-foreground">City</label>
                            <Input
                              value={editForm.billing_city || ''}
                              onChange={(e) =>
                                handleEditFieldChange('billing_city', e.target.value)
                              }
                              className="h-9"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground">State/Province</label>
                            <Input
                              value={editForm.billing_state || ''}
                              onChange={(e) =>
                                handleEditFieldChange('billing_state', e.target.value)
                              }
                              className="h-9"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-muted-foreground">Postal Code</label>
                            <Input
                              value={editForm.billing_postal_code || ''}
                              onChange={(e) =>
                                handleEditFieldChange('billing_postal_code', e.target.value)
                              }
                              className="h-9"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground">Country</label>
                            <Input
                              value={editForm.billing_country || ''}
                              onChange={(e) =>
                                handleEditFieldChange('billing_country', e.target.value)
                              }
                              className="h-9"
                            />
                          </div>
                        </div>
                      </dd>
                    </div>
                  ) : (
                    <div className="py-2 border-b border-border">
                      <dt className="text-sm font-medium text-muted-foreground">Billing Address</dt>
                      <dd className="mt-0.5 text-sm">
                        {order?.billing_street && <div>{order.billing_street}</div>}
                        {(order?.billing_city ||
                          order?.billing_state ||
                          order?.billing_postal_code) && (
                          <div>
                            {[order?.billing_city, order?.billing_state, order?.billing_postal_code]
                              .filter(Boolean)
                              .join(', ')}
                          </div>
                        )}
                        {order?.billing_country && <div>{order.billing_country}</div>}
                        {!order?.billing_street && !order?.billing_city && 'N/A'}
                      </dd>
                    </div>
                  )}
                </dl>
              </CardContent>
            </Card>
          </div>

          {/* Shipping Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" aria-hidden="true" />
                Shipping Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-0">
                {isEditMode ? (
                  <div className="py-2 border-b border-border">
                    <dt className="text-sm font-medium text-muted-foreground mb-1">
                      Shipping Address
                    </dt>
                    <dd className="space-y-2">
                      <AddressAutocomplete
                        id="shipping_street"
                        label="Shipping Street"
                        value={editForm.shipping_street || ''}
                        onAddressSelect={handleShippingAddressSelect}
                        onStreetChange={(street) =>
                          handleEditFieldChange('shipping_street', street)
                        }
                        countryRestrictions={['us', 'ca']}
                        placeholder="Start typing address..."
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-muted-foreground">City</label>
                          <Input
                            value={editForm.shipping_city || ''}
                            onChange={(e) => handleEditFieldChange('shipping_city', e.target.value)}
                            className="h-9"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground">State/Province</label>
                          <Input
                            value={editForm.shipping_state || ''}
                            onChange={(e) =>
                              handleEditFieldChange('shipping_state', e.target.value)
                            }
                            className="h-9"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-muted-foreground">Postal Code</label>
                          <Input
                            value={editForm.shipping_postal_code || ''}
                            onChange={(e) =>
                              handleEditFieldChange('shipping_postal_code', e.target.value)
                            }
                            className="h-9"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground">Country</label>
                          <Input
                            value={editForm.shipping_country || ''}
                            onChange={(e) =>
                              handleEditFieldChange('shipping_country', e.target.value)
                            }
                            className="h-9"
                          />
                        </div>
                      </div>
                    </dd>
                  </div>
                ) : (
                  <div className="py-2 border-b border-border">
                    <dt className="text-sm font-medium text-muted-foreground">Shipping Address</dt>
                    <dd className="mt-0.5 text-sm">
                      {order?.shipping_street && <div>{order.shipping_street}</div>}
                      {(order?.shipping_city ||
                        order?.shipping_state ||
                        order?.shipping_postal_code) && (
                        <div>
                          {[
                            order?.shipping_city,
                            order?.shipping_state,
                            order?.shipping_postal_code,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </div>
                      )}
                      {order?.shipping_country && <div>{order.shipping_country}</div>}
                      {!order?.shipping_street && !order?.shipping_city && 'N/A'}
                    </dd>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>

          {/* Additional Information */}
          {!isNew && (
            <Card>
              <CardHeader>
                <CardTitle>Additional Information</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-0">
                  <div className="py-2 border-b border-border">
                    <dt className="text-sm font-medium text-muted-foreground">Created</dt>
                    <dd className="mt-0.5 text-sm">
                      {formatDateTime(order?.created_date)}
                      {order?.created_by_name && (
                        <span className="text-muted-foreground"> by {order.created_by_name}</span>
                      )}
                    </dd>
                  </div>
                  <div className="py-2 border-b border-border">
                    <dt className="text-sm font-medium text-muted-foreground">Last Modified</dt>
                    <dd className="mt-0.5 text-sm">
                      {formatDateTime(order?.last_modified_date)}
                      {order?.last_modified_by_name && (
                        <span className="text-muted-foreground">
                          {' '}
                          by {order.last_modified_by_name}
                        </span>
                      )}
                    </dd>
                  </div>
                  {order?.approved_at && (
                    <div className="py-2 border-b border-border">
                      <dt className="text-sm font-medium text-muted-foreground">Approved Date</dt>
                      <dd className="mt-0.5 text-sm">{formatDateTime(order?.approved_at)}</dd>
                    </div>
                  )}
                  {order?.approved_by_name && (
                    <div className="py-2 border-b border-border">
                      <dt className="text-sm font-medium text-muted-foreground">Approved By</dt>
                      <dd className="mt-0.5 text-sm">{order.approved_by_name}</dd>
                    </div>
                  )}
                </dl>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Line Items Tab */}
        {!isNew && (
          <TabsContent value="items">
            <Card>
              <CardHeader>
                <CardTitle>Order Line Items</CardTitle>
                <CardDescription>Products and services included in this order</CardDescription>
              </CardHeader>
              <CardContent>
                {orderItems.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No line items</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" role="table">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 font-medium text-sm">Product</th>
                          <th className="text-left py-2 font-medium text-sm">Code</th>
                          <th className="text-right py-2 font-medium text-sm">Quantity</th>
                          <th className="text-right py-2 font-medium text-sm">Unit Price</th>
                          <th className="text-right py-2 font-medium text-sm">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderItems.map((item) => {
                          const familyKey = matchProductFamily({
                            name: item.product_name,
                            family: item.product_family,
                          })
                          const termLabel = SUBSCRIPTION_TERM_OPTIONS.find(
                            (o) => o.value === item.subscription_term_months
                          )?.label
                          const hasExtras = Boolean(
                            item.domain_users_text ||
                              item.subscription_term_months ||
                              item.monthly_delivery_day ||
                              item.primary_contact_id
                          )
                          return (
                            <Fragment key={item.id}>
                              <tr className={hasExtras ? '' : 'border-b'}>
                                <td className="py-2">{item.product_name || 'Unknown Product'}</td>
                                <td className="py-2 text-muted-foreground">
                                  {item.product_code || '-'}
                                </td>
                                <td className="py-2 text-right">{item.quantity}</td>
                                <td className="py-2 text-right">
                                  {formatCurrency(item.unit_price)}
                                </td>
                                <td className="py-2 text-right font-medium">
                                  {formatCurrency(item.total_price)}
                                </td>
                              </tr>
                              {Number(item.discount) > 0 && (
                                <tr style={{ backgroundColor: CATEGORICAL.raspberry.tint }}>
                                  <td
                                    colSpan={4}
                                    className="py-1.5 pl-4 text-sm"
                                    style={{ color: GRACKLE_COLORS.darkRaspberry }}
                                  >
                                    Discount
                                    {item.list_price
                                      ? ` (was ${formatCurrency(item.list_price)} / unit)`
                                      : ''}
                                  </td>
                                  <td
                                    className="py-1.5 text-right text-sm font-medium"
                                    style={{ color: GRACKLE_COLORS.darkRaspberry }}
                                  >
                                    -{formatCurrency(item.discount)}
                                  </td>
                                </tr>
                              )}
                              {hasExtras && (
                                <tr className="border-b">
                                  <td
                                    colSpan={5}
                                    className="pb-2 pl-4 text-xs text-muted-foreground"
                                  >
                                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                                      {item.domain_users_text && (
                                        <span>
                                          <strong>
                                            {familyKey
                                              ? DOMAIN_USERS_LABEL_BY_FAMILY[familyKey]
                                              : 'Domain/User(s)'}
                                            :
                                          </strong>{' '}
                                          {item.domain_users_text}
                                        </span>
                                      )}
                                      {item.subscription_term_months && (
                                        <span>
                                          <strong>Subscription term:</strong>{' '}
                                          {termLabel || `${item.subscription_term_months} months`}
                                        </span>
                                      )}
                                      {item.monthly_delivery_day && (
                                        <span>
                                          <strong>Monthly delivery date:</strong> day{' '}
                                          {item.monthly_delivery_day}
                                        </span>
                                      )}
                                      {item.primary_contact_id && (
                                        <span>
                                          <strong>Main point of contact:</strong>{' '}
                                          {item.primary_contact_id}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          )
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="font-bold">
                          <td colSpan={4} className="py-2 text-right">
                            Total:
                          </td>
                          <td className="py-2 text-right">{formatCurrency(calculatedTotal)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Notes Tab */}
        {!isNew && (
          <TabsContent value="notes">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" aria-hidden="true" />
                    Notes
                  </CardTitle>
                  <CardDescription>Notes and comments for this order</CardDescription>
                </div>
                <Button onClick={() => setShowNoteForm(true)} size="sm" aria-label="Add new note">
                  <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                  Add Note
                </Button>
              </CardHeader>
              <CardContent>
                {/* New Note Form */}
                {showNoteForm && (
                  <div className="mb-6 p-4 border rounded-lg bg-muted/30">
                    <h3 className="text-sm font-medium mb-3">New Note</h3>
                    <div className="space-y-3">
                      <div>
                        <label htmlFor="note-title" className="text-sm font-medium">
                          Title (optional)
                        </label>
                        <Input
                          id="note-title"
                          value={newNoteTitle}
                          onChange={(e) => setNewNoteTitle(e.target.value)}
                          placeholder="Note title..."
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label htmlFor="note-body" className="text-sm font-medium">
                          Content <span className="text-destructive">*</span>
                        </label>
                        <textarea
                          id="note-body"
                          value={newNoteBody}
                          onChange={(e) => setNewNoteBody(e.target.value)}
                          placeholder="Enter note content..."
                          className="mt-1 flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          aria-required="true"
                        />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowNoteForm(false)
                            setNewNoteTitle('')
                            setNewNoteBody('')
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleCreateNote}
                          disabled={createNoteMutation.isPending}
                        >
                          {createNoteMutation.isPending ? 'Saving...' : 'Save Note'}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes List */}
                {notes.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare
                      className="h-12 w-12 text-muted-foreground mx-auto mb-4"
                      aria-hidden="true"
                    />
                    <p className="text-muted-foreground">No notes for this order yet.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {notes.map((note: Note) => (
                      <div
                        key={note.id}
                        className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <button
                              onClick={() => {
                                setSelectedNoteId(note.id)
                                setNoteModalOpen(true)
                              }}
                              className="text-sm font-medium hover:underline text-left focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                              aria-label={`View note: ${note.title || 'Untitled'}`}
                            >
                              {note.title || 'Untitled Note'}
                            </button>
                            {note.body && (
                              <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                                {note.body}
                              </p>
                            )}
                            <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                              <span>{note.owner_name || 'Unknown'}</span>
                              <span>•</span>
                              <span>{formatDate(note.created_date)}</span>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteNote(note.id, note.title || '')}
                            disabled={deleteNoteMutation.isPending}
                            aria-label={`Delete note: ${note.title || 'Untitled'}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Note Detail Modal */}
            <NoteDetailModal
              noteId={selectedNoteId}
              open={noteModalOpen}
              onOpenChange={setNoteModalOpen}
            />
          </TabsContent>
        )}

        {/* Files Tab */}
        {!isNew && (
          <TabsContent value="files" className="space-y-4">
            <AttachmentsTab entityId={id!} entityType="Order" />
          </TabsContent>
        )}

        {/* History Tab */}
        {!isNew && (
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Order History</CardTitle>
                <CardDescription>Track changes made to this order</CardDescription>
              </CardHeader>
              <CardContent>
                <HistoryTimeline entityId={id!} entityType="Order" />
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
      <EditActionBar
        isEditMode={isEditMode}
        onSave={handleSaveEdit}
        onCancel={handleCancelEdit}
        isSaving={updateOrderMutation.isPending}
        saveLabel="Save Changes"
      />

      {/* Email the customer. Rendered only with a real order behind it, since the
          dialog reads the order's products and billing contact. */}
      {order && (
        <OrderEmailDialog order={order} open={showEmailDialog} onOpenChange={setShowEmailDialog} />
      )}

      {/* Order Denial Modal */}
      <Dialog open={showDenialModal} onOpenChange={setShowDenialModal}>
        <DialogContent aria-describedby="denial-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" aria-hidden="true" />
              Deny Order
            </DialogTitle>
            <DialogDescription id="denial-description">
              Please provide a reason for denying this order. The order owner will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-muted rounded-lg text-sm space-y-1">
              <p>
                <span className="font-medium">Order:</span>{' '}
                {order?.order_number || order?.name || '-'}
              </p>
              <p>
                <span className="font-medium">Account:</span> {order?.account_name || '-'}
              </p>
              <p>
                <span className="font-medium">Amount:</span> {formatCurrency(order?.total_amount)}
              </p>
              <p>
                <span className="font-medium">Owner:</span> {order?.owner_name || '-'}
              </p>
            </div>
            <div>
              <Label htmlFor="denial-notes">Denial Notes *</Label>
              <textarea
                id="denial-notes"
                value={denialNotes}
                onChange={(e) => setDenialNotes(e.target.value)}
                placeholder="Reason for denial..."
                className="mt-1 flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-required="true"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDenialModal(false)
                  setDenialNotes('')
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDenyOrder}
                disabled={isDenying || !denialNotes.trim()}
              >
                {isDenying ? 'Denying...' : 'Deny Order & Notify'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  )
}
